import React, { useState } from 'react';
import { ChevronLeft, Lock, Bug } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
interface CreateNewPasswordProps {
  onBack: () => void;
  onSuccess: () => void;
}
export function CreateNewPassword({
  onBack,
  onSuccess
}: CreateNewPasswordProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDebug, setShowDebug] = useState(false);
  const validatePassword = (value: string): string => {
    if (!value) return 'Enter a new password';
    if (value.length < 6) return 'Password must be at least 6 characters';
    if (!/^[a-zA-Z0-9]+$/.test(value)) return 'Password must be alphanumeric';
    return '';
  };
  const validateConfirmPassword = (value: string, passwordValue: string): string => {
    if (!value) return 'Confirm your new password';
    if (value !== passwordValue) return 'Passwords do not match';
    return '';
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    newErrors.newPassword = validatePassword(newPassword);
    newErrors.confirmPassword = validateConfirmPassword(confirmPassword, newPassword);
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) {
      return;
    }
    onSuccess();
  };
  const loadDebugState = (state: string) => {
    setShowDebug(false);
    setErrors({});
    switch (state) {
      case 'empty':
        setNewPassword('');
        setConfirmPassword('');
        break;
      case 'too-short':
        setNewPassword('abc');
        setConfirmPassword('abc');
        setErrors({
          newPassword: 'Password must be at least 6 characters'
        });
        break;
      case 'not-alphanumeric':
        setNewPassword('pass@123');
        setConfirmPassword('pass@123');
        setErrors({
          newPassword: 'Password must be alphanumeric'
        });
        break;
      case 'mismatch':
        setNewPassword('password123');
        setConfirmPassword('password456');
        setErrors({
          confirmPassword: 'Passwords do not match'
        });
        break;
      case 'valid':
        setNewPassword('password123');
        setConfirmPassword('password123');
        break;
    }
  };
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-y-auto relative" data-id="element-478">
      {/* Debug Toggle */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-40 w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-479">
        <Bug size={14} data-id="element-480" />
      </button>

      {/* Debug Panel */}
      {showDebug && <div className="absolute top-16 right-2 z-40 bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-56 text-xs animate-in fade-in zoom-in-95 duration-150" data-id="element-481">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-482">
            Debug Panel
          </p>
          <button onClick={() => loadDebugState('empty')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-483">
            ▶ Empty state
          </button>
          <button onClick={() => loadDebugState('too-short')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-484">
            ▶ Password too short
          </button>
          <button onClick={() => loadDebugState('not-alphanumeric')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-485">
            ▶ Not alphanumeric
          </button>
          <button onClick={() => loadDebugState('mismatch')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-486">
            ▶ Password mismatch
          </button>
          <button onClick={() => loadDebugState('valid')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-487">
            ▶ Valid passwords
          </button>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 sticky top-0 bg-stone-50/90 backdrop-blur-md z-20" data-id="element-488">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-489">
          <ChevronLeft size={28} data-id="element-490" />
        </button>
        <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-white shadow-sm bg-stone-200" data-id="element-491">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" src="https://cdn.magicpatterns.com/uploads/69mUG6ooMsKbP89Ta37mkt/Katethought_friendly_gym_mascot_character_funny_good_guy_musc_bc3cfd10-111a-4766-8e76-81af2852edc8_2.mp4" data-id="element-492" />
        </div>
        <div className="w-10" data-id="element-493" />
      </div>

      <div className="px-6 pb-8 flex-1 flex flex-col" data-id="element-494">
        {/* Title Area */}
        <div className="mb-8 mt-2" data-id="element-495">
          <h1 className="text-3xl font-extrabold text-slate-800 mb-2" data-id="element-496">
            Create new password
          </h1>
          <p className="text-slate-500 font-medium leading-relaxed" data-id="element-497">
            Enter your new password below.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5 flex-1" data-id="element-498">
          <Input label="New Password" icon={Lock} type="password" placeholder="••••••••" value={newPassword} onChange={e => {
          setNewPassword(e.target.value);
          setErrors({
            ...errors,
            newPassword: ''
          });
        }} error={errors.newPassword} data-id="element-499" />

          <Input label="Confirm New Password" icon={Lock} type="password" placeholder="••••••••" value={confirmPassword} onChange={e => {
          setConfirmPassword(e.target.value);
          setErrors({
            ...errors,
            confirmPassword: ''
          });
        }} error={errors.confirmPassword} data-id="element-500" />

          <div className="pt-6 space-y-4" data-id="element-501">
            <Button type="submit" variant="primary" fullWidth data-id="element-502">
              Save password
            </Button>

            <button type="button" onClick={onBack} className="w-full text-center text-sm font-bold text-slate-600 hover:text-slate-800 py-2" data-id="element-503">
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>;
}